import { MINUTE } from '@gorila-shared-ui/components';
import mpegts from 'mpegts.js';
import { useEffect, useRef, useState } from 'react';
import useUpdateEffect from '../../../hooks/useUpdateEffect';

type Props = {
  initialVolume: number;
  playerRef: React.RefObject<HTMLVideoElement>;
  src: string;
  onBadRequest: () => void;
  onLoadEnd?: () => void;
  onDisconnect: () => void;
};

function MpegTsPlayer({
  initialVolume,
  muted = false,
  playerRef,
  src,
  onBadRequest,
  onDisconnect,
  onLoadEnd,
  ...props
}: Readonly<Props & Omit<React.VideoHTMLAttributes<HTMLVideoElement>, 'controlsList'>>) {
  const [playing, setPlaying] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const playerInstance = useRef<mpegts.Player | null>(null);

  useEffect(() => {
    const volumeTimeout = setTimeout(() => {
      if (playerRef.current) {
        playerRef.current.volume = initialVolume;
      }
    }, 0);
    return () => {
      clearTimeout(volumeTimeout);
    };
  }, [playerRef]);

  const onPlaying = (isPlaying: boolean, callback?: () => void) => {
    setPlaying(isPlaying);
    callback?.();
  };

  useUpdateEffect(() => {
    const timeout = setTimeout(() => {
      onDisconnect();
    }, MINUTE * 1.5);
    return () => {
      clearTimeout(timeout);
    };
  }, [playing]);

  useEffect(() => {
    const video = playerRef.current;

    if (!video) return;

    if (!mpegts.getFeatureList().mseLivePlayback) {
      console.error('MSE Live Playback not supported');
      return;
    }

    if (!playerInstance.current) {
      const playerx = mpegts.createPlayer(
        {
          type: 'flv',
          isLive: true,
          url: src,
        },
        {
          enableWorker: true,
          lazyLoad: false,
          liveBufferLatencyChasing: true, // NOTE: remove to not move to live.
          liveBufferLatencyMaxLatency: 2, // NOTE: remove to not move to live.
          liveBufferLatencyMinRemain: 0.5, // NOTE: remove to not move to live.
          autoCleanupSourceBuffer: true, // NOTE: set to false to let seek video.
        },
      );
      playerInstance.current = playerx;
    }

    const player = playerInstance.current;

    player.attachMediaElement(video);

    player.on(mpegts.Events.LOADING_COMPLETE, (info) => {
      onDisconnect();
    });

    player.on(mpegts.Events.ERROR, (errorType, errorDetail, errorInfo) => {
      console.error('[mpegts]', errorType, errorDetail, errorInfo);

      // Network errors
      if (errorType === mpegts.ErrorTypes.NETWORK_ERROR) {
        onPlaying(false, () => {
          onBadRequest();
        });
      }
    });

    const handlePlay = () => {
      onPlaying(true);
    };

    const handlePause = () => {
      onPlaying(false);
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener(
      'loadeddata',
      () => {
        onLoadEnd?.();
        setLoaded(true);
      },
      { once: true },
    );
    video.addEventListener('error', () => {
      onLoadEnd?.();
    });

    player.load();

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);

      player.pause();
      player.unload();
      player.detachMediaElement();
      player.destroy();

      playerInstance.current = null;
      setPlaying(false);

      video.removeAttribute('src');
      video.load();
    };
  }, [src]);

  useEffect(() => {
    const player = playerInstance.current;
    const video = playerRef.current;

    if (!player || !video) {
      return;
    }

    if (loaded) {
      // AUTOPLAY - RESUME
      video.play().catch((err) => {
        console.error('Autoplay failed', err);
      });
    }
  }, [playerRef, loaded]);

  return (
    <video
      {...props}
      ref={playerRef}
      autoPlay
      playsInline
      controlsList="nofullscreen nodownload"
      preload="none"
    />
  );
}

export default MpegTsPlayer;
